import type * as Sentry from '@sentry/react';
import type { BoxProps, StackProps, TabsProps } from '@mui/material';
import { IconButton } from '@mui/material';
import { Box, Breadcrumbs, Divider, Typography } from '@mui/material';
import Stack from '@mui/material/Stack';
import { VyIcon } from '@weft/icons';
import type { PartialNever } from '@weft/utils';
import { isString } from '@weft/utils';
import { isNotEmptyArray } from '@weft/utils';
import React, { useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import { useVyNavigate } from '../../../hooks/use-vy-navigate';
import { isSandboxEnvironment } from '../../../utils/env-indicator';
import { SideNavbar } from '../../navigation/NewSideNavbar/SideNavbar';
import { SuspenseBoundary } from '../../utility/SuspenseBoundary';
import { VyPageLayoutTabs, type VyPageLayoutRequiredTabsProps } from './components/VyPageLayoutTabs';

export type VyPageLayoutProps = Omit<BoxProps, 'title'> & {
  title: React.ReactNode;
  path?: [React.ReactNode | {
    href: string;
    label: React.ReactNode;
  }, ...(React.ReactNode | {
    href: string;
    label: React.ReactNode;
  })[]];
  hideHeader?: boolean;
  action?: React.ReactNode;
  tabsProps?: TabsProps;
  tabsAction?: React.ReactNode;
  headerProps?: StackProps;
  errorBoundaryProps?: Sentry.ErrorBoundaryProps;
  showBackButton?: boolean;
} & (Required<VyPageLayoutRequiredTabsProps> | PartialNever<VyPageLayoutRequiredTabsProps>);

export const VyPageLayout: React.FC<VyPageLayoutProps> = ({
  children,
  title,
  path,
  hideHeader = false,
  tabs,
  currentTab,
  action,
  tabsProps,
  tabsAction,
  headerProps,
  errorBoundaryProps,
  showBackButton = false,
  ...props
}) => {
  const navigate = useVyNavigate();
  const containerRef = React.useRef<HTMLDivElement>(null);

  const dividerHeight = 1;

  const pathHeight = 56;
  const titleHeight = 72;
  const tabsHeight = 42;

  const height = useMemo(() => {
    let totalHeight = titleHeight + dividerHeight;

    if (isNotEmptyArray(path)) {
      totalHeight += pathHeight + dividerHeight;
    }

    if (isNotEmptyArray(tabs)) {
      totalHeight += tabsHeight;
    }

    return totalHeight;
  }, [path, tabs]);

  return (
    <div
      className="flex overflow-hidden"
    >
      <div>
        <SideNavbar/>
      </div>

      <div className="flex-1 overflow-hidden">
        {!hideHeader && (
          <>
            <Stack
              direction="row"
              justifyContent="space-between"
              {...headerProps}
            >
              <Stack
                component="header"
                position="relative"
                width={1}
              >
                {isNotEmptyArray(path) && (
                  <Breadcrumbs sx={{
                    height: `${pathHeight}px`,
                    maxHeight: `${pathHeight}px`,
                    padding: '0 48px',
                    alignItems: 'center',
                    display: 'flex',
                  }}
                  >
                    {path.map((item, index) => {
                      if (!isPathNavigateItem(item)) {
                        return (
                          <Typography
                            key={crypto.randomUUID()}
                            color={index === path.length - 1 ? 'text.primary' : 'text.secondary'}
                          >
                            {item}
                          </Typography>
                        );
                      }

                      return (
                        <NavLink
                          key={crypto.randomUUID()}
                          color={index === path.length - 1 ? 'text.primary' : 'text.secondary'}
                          to={item.href}
                        >
                          {item.label}
                        </NavLink>
                      );
                    })}
                  </Breadcrumbs>
                )}

                {isNotEmptyArray(path) && (<Divider/>)}

                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{
                    height: `${titleHeight}px`,
                    maxHeight: `${titleHeight}px`,
                    padding: '24px 24px 16px 24px',
                    alignItems: 'center',
                    display: 'flex',
                  }}
                >
                  <Stack
                    direction="row"
                    columnGap={2}
                    alignItems="center"
                  >
                    {showBackButton && (
                      <IconButton
                        sx={{
                          border: '1px solid #0D1B271A',
                          borderRadius: '4px',
                          color: '#323232',
                          width: '32px',
                          height: '32px',
                        }}
                        onClick={() => {
                          navigate(-1);
                        }}
                      >
                        <VyIcon
                          icon="ArrowTopRight"
                          style={{
                            transform: 'rotate(-135deg)',
                          }}
                        />
                      </IconButton>
                    )}

                    {isString(title) ? (
                      <Typography
                        variant="h5"
                        alignItems="center"
                      >
                        {title}
                      </Typography>
                    ) : (title)}
                  </Stack>

                  {action}
                </Stack>
              </Stack>
            </Stack>
          </>
        )}

        {isNotEmptyArray(tabs) && (
          <Box sx={{
            height: `${tabsHeight}px`,
            maxHeight: `${tabsHeight}px`,
            padding: '0 24px',
          }}
          >
            <VyPageLayoutTabs
              tabs={tabs}
              currentTab={currentTab}
              containerRef={containerRef}
              tabsAction={tabsAction}
              {...tabsProps}
            />
          </Box>
        )}

        <Divider/>

        <Box
          ref={containerRef}
          component="main"
          {...props}
          sx={{
            backgroundColor: 'background.default',
            height: `calc(100vh - ${height}px - ${isSandboxEnvironment() ? '52px' : '0px'})`,
            padding: '40px',
            width: '100%',
            overflow: 'hidden',
            overflowY: 'auto',
            ...props?.sx,
          }}
        >
          <SuspenseBoundary errorBoundaryProps={errorBoundaryProps}>
            {children}
          </SuspenseBoundary>
        </Box>
      </div>
    </div>
  );
};

export function withPageLayout<ComponentProps extends Record<string, unknown>>(
  Component: React.ComponentType<ComponentProps>,
  options: VyPageLayoutProps,
): React.FC<ComponentProps> {
  return (props) => (
    <VyPageLayout {...options}>
      <Component {...props}/>
    </VyPageLayout>
  );
}

export function withPageLayoutTabs<ComponentProps extends Record<string, unknown>>(
  Component: React.ComponentType<ComponentProps>,
  options: VyPageLayoutProps,
  
  useTabs: () => [Required<VyPageLayoutRequiredTabsProps>['tabs'], Required<VyPageLayoutRequiredTabsProps>['currentTab']],
): React.FC<ComponentProps> {
  return (props) => {
    const [tabs, currentTab] = useTabs();

    return (
      <VyPageLayout
        {...options}
        tabs={tabs}
        currentTab={currentTab}
      >
        <Component {...props}/>
      </VyPageLayout>
    );
  };
}

function isPathNavigateItem(item: unknown): item is {
  href: string;
  label: React.ReactNode;
} {
  return typeof item === 'object' && item !== null && 'href' in item && 'label' in item;
}